import { useState, useEffect, useCallback, useRef } from "react";
import { useNavigate } from "react-router-dom";
import Highcharts from "highcharts";
import HighchartsReact from "highcharts-react-official";
import { GoogleMap, useLoadScript } from "@react-google-maps/api";
import { authFetch } from "../api";
import Header from "../components/Header";
import NeighborhoodSelect from "../components/NeighborhoodSelect";

const BEDROOM_OPTIONS = [
  { value: "", label: "All Bedrooms" },
  { value: "0", label: "Studio" },
  { value: "1", label: "1 BR" },
  { value: "2", label: "2 BR" },
  { value: "3", label: "3 BR" },
  { value: "4", label: "4+ BR" },
];

const COLORS = {
  "Studio": "#e91e63",
  "1BR": "#2196f3",
  "2BR": "#4caf50",
  "3BR": "#ff9800",
  "4BR+": "#9c27b0",
};

const mapContainerStyle = { width: "100%", height: "100%" };
const defaultCenter = { lat: 40.7167, lng: -73.956 };

export default function RentalStatsPage() {
  const navigate = useNavigate();
  useEffect(() => {
    if (!localStorage.getItem("token")) navigate("/login");
  }, [navigate]);

  const { isLoaded } = useLoadScript({
    googleMapsApiKey: process.env.REACT_APP_GOOGLE_MAPS_API_KEY,
    libraries: ["visualization"],
  });

  const [tab, setTab] = useState("chart");
  const [neighborhoods, setNeighborhoods] = useState([]);
  const [selectedHood, setSelectedHood] = useState(null);
  const [bedrooms, setBedrooms] = useState("");
  const [statsData, setStatsData] = useState(null);
  const [heatData, setHeatData] = useState(null);
  const [loading, setLoading] = useState(false);
  const mapRef = useRef(null);
  const heatmapLayerRef = useRef(null);

  // Load neighborhoods
  useEffect(() => {
    authFetch("/sale-reviews/neighborhoods")
      .then((data) => {
        setNeighborhoods(data.neighborhoods || []);
        const defaults = data.default_neighborhoods || [];
        if (defaults.length > 0) {
          const firstId = typeof defaults[0] === "number" ? defaults[0] : null;
          if (firstId) setSelectedHood(firstId);
        }
      })
      .catch(console.error);
  }, []);

  // Fetch chart data
  useEffect(() => {
    if (!selectedHood) return;
    setLoading(true);
    const bedParam = bedrooms ? `&bedrooms=${bedrooms}` : "";
    authFetch(`/rental-stats/${selectedHood}?from_date=2020-01-01${bedParam}`)
      .then((data) => {
        setStatsData(data);
        setLoading(false);
      })
      .catch((err) => {
        console.error(err);
        setLoading(false);
      });
  }, [selectedHood, bedrooms]);

  // Fetch heat map data
  useEffect(() => {
    if (!selectedHood || tab !== "map") return;
    const bedParam = bedrooms ? `&bedrooms=${bedrooms}` : "";
    authFetch(`/rental-stats/heatmap/${selectedHood}?months=6${bedParam}`)
      .then(setHeatData)
      .catch(console.error);
  }, [selectedHood, bedrooms, tab]);

  // Build Highcharts options
  const chartOptions = useCallback(() => {
    if (!statsData?.by_bedroom) return {};

    const series = [];
    for (const [label, months] of Object.entries(statsData.by_bedroom)) {
      series.push({
        name: `${label} Median`,
        type: "line",
        color: COLORS[label] || "#666",
        data: months.map((m) => [new Date(m.month).getTime(), m.median_rent]),
        marker: { enabled: false },
        lineWidth: 2,
      });
      series.push({
        name: `${label} Avg`,
        type: "line",
        color: COLORS[label] || "#666",
        data: months.map((m) => [new Date(m.month).getTime(), m.avg_rent]),
        dashStyle: "Dash",
        marker: { enabled: false },
        lineWidth: 1,
        visible: false,
      });
    }

    return {
      title: { text: `Rental Prices — ${statsData.neighborhood_name || ""}` },
      xAxis: { type: "datetime", title: { text: "Month" } },
      yAxis: {
        title: { text: "Rent ($/mo)" },
        labels: { formatter: function () { return "$" + Highcharts.numberFormat(this.value, 0); } },
      },
      tooltip: {
        shared: true,
        crosshairs: true,
        valuePrefix: "$",
        valueDecimals: 0,
      },
      legend: { enabled: true },
      series,
      chart: { height: 500, zoomType: "x" },
      credits: { enabled: false },
    };
  }, [statsData]);

  // Render heat map layer
  const onMapLoad = useCallback((map) => {
    mapRef.current = map;
  }, []);

  useEffect(() => {
    if (!isLoaded || !mapRef.current || !heatData?.points?.length) return;

    // Remove old layer
    if (heatmapLayerRef.current) {
      heatmapLayerRef.current.setMap(null);
    }

    const points = heatData.points.map((p) => ({
      location: new window.google.maps.LatLng(p.lat, p.lng),
      weight: p.rent / 1000,
    }));

    heatmapLayerRef.current = new window.google.maps.visualization.HeatmapLayer({
      data: points,
      map: mapRef.current,
      radius: 30,
      opacity: 0.7,
    });

    // Fit bounds
    if (points.length > 0) {
      const bounds = new window.google.maps.LatLngBounds();
      points.forEach((p) => bounds.extend(p.location));
      mapRef.current.fitBounds(bounds);
    }
  }, [isLoaded, heatData]);

  // Summary cards
  const summaryCards = statsData?.summary ? Object.entries(statsData.summary) : [];

  const tabStyle = (active) => ({
    padding: "10px 24px",
    fontSize: "14px",
    fontWeight: active ? "700" : "500",
    color: active ? "#1976d2" : "#666",
    borderBottom: active ? "3px solid #1976d2" : "3px solid transparent",
    cursor: "pointer",
    backgroundColor: "transparent",
    border: "none",
    borderBottomStyle: "solid",
  });

  return (
    <div style={{ minHeight: "100vh", backgroundColor: "#f5f5f5" }}>
      <Header showLogout={true} />

      <div style={{ maxWidth: "1400px", margin: "0 auto", padding: "20px" }}>
        {/* Filters */}
        <div style={{
          display: "flex", gap: "16px", alignItems: "flex-end",
          backgroundColor: "#fff", padding: "16px", borderRadius: "8px",
          marginBottom: "20px", boxShadow: "0 2px 4px rgba(0,0,0,0.1)",
        }}>
          <div style={{ minWidth: "300px" }}>
            <label style={{ display: "block", fontSize: "12px", fontWeight: "600", marginBottom: "4px", color: "#495057" }}>
              Neighborhood
            </label>
            <select
              value={selectedHood || ""}
              onChange={(e) => setSelectedHood(e.target.value ? parseInt(e.target.value) : null)}
              style={{ width: "100%", padding: "8px", borderRadius: "4px", border: "1px solid #ced4da", fontSize: "13px" }}
            >
              <option value="">Select neighborhood</option>
              {neighborhoods.map((n) => (
                <option key={n.id} value={n.id}>{n.name}</option>
              ))}
            </select>
          </div>

          <div>
            <label style={{ display: "block", fontSize: "12px", fontWeight: "600", marginBottom: "4px", color: "#495057" }}>
              Bedrooms
            </label>
            <select
              value={bedrooms}
              onChange={(e) => setBedrooms(e.target.value)}
              style={{ padding: "8px", borderRadius: "4px", border: "1px solid #ced4da", fontSize: "13px" }}
            >
              {BEDROOM_OPTIONS.map((o) => (
                <option key={o.value} value={o.value}>{o.label}</option>
              ))}
            </select>
          </div>
        </div>

        {/* Summary Cards */}
        {summaryCards.length > 0 && (
          <div style={{ display: "flex", gap: "12px", marginBottom: "20px", flexWrap: "wrap" }}>
            {summaryCards.map(([label, s]) => (
              <div key={label} style={{
                flex: "1", minWidth: "150px", backgroundColor: "#fff", padding: "16px",
                borderRadius: "8px", boxShadow: "0 2px 4px rgba(0,0,0,0.1)",
                borderLeft: `4px solid ${COLORS[label] || "#666"}`,
              }}>
                <div style={{ fontSize: "12px", fontWeight: "600", color: "#666", marginBottom: "4px" }}>{label}</div>
                <div style={{ fontSize: "24px", fontWeight: "700" }}>
                  ${s.latest_median?.toLocaleString() || "—"}
                </div>
                {s.yoy_change_pct !== null && (
                  <div style={{
                    fontSize: "13px", fontWeight: "600", marginTop: "4px",
                    color: s.yoy_change_pct > 0 ? "#c62828" : s.yoy_change_pct < 0 ? "#2e7d32" : "#666",
                  }}>
                    {s.yoy_change_pct > 0 ? "↑" : s.yoy_change_pct < 0 ? "↓" : "→"} {Math.abs(s.yoy_change_pct)}% YoY
                  </div>
                )}
                <div style={{ fontSize: "11px", color: "#999", marginTop: "4px" }}>
                  {s.months_of_data} months · {s.total_listings?.toLocaleString()} listings
                </div>
              </div>
            ))}
          </div>
        )}

        {/* Tabs */}
        <div style={{ display: "flex", gap: "0", borderBottom: "1px solid #ddd", marginBottom: "20px" }}>
          <button style={tabStyle(tab === "chart")} onClick={() => setTab("chart")}>
            Trend Chart
          </button>
          <button style={tabStyle(tab === "map")} onClick={() => setTab("map")}>
            Heat Map
          </button>
        </div>

        {/* Tab Content */}
        {tab === "chart" && (
          <div style={{
            backgroundColor: "#fff", borderRadius: "8px", padding: "20px",
            boxShadow: "0 2px 4px rgba(0,0,0,0.1)",
          }}>
            {loading ? (
              <div style={{ textAlign: "center", padding: "60px", color: "#666" }}>Loading...</div>
            ) : statsData?.by_bedroom ? (
              <HighchartsReact highcharts={Highcharts} options={chartOptions()} />
            ) : (
              <div style={{ textAlign: "center", padding: "60px", color: "#666" }}>
                Select a neighborhood to view rental trends
              </div>
            )}
          </div>
        )}

        {tab === "map" && (
          <div style={{
            backgroundColor: "#fff", borderRadius: "8px", overflow: "hidden",
            boxShadow: "0 2px 4px rgba(0,0,0,0.1)", height: "600px",
            position: "relative",
          }}>
            {!isLoaded ? (
              <div style={{ textAlign: "center", padding: "60px", color: "#666" }}>Loading map...</div>
            ) : (
              <GoogleMap
                mapContainerStyle={mapContainerStyle}
                center={defaultCenter}
                zoom={14}
                onLoad={onMapLoad}
              />
            )}
            {/* Heat Map Legend */}
            <div style={{
              position: "absolute", bottom: "20px", left: "20px",
              backgroundColor: "rgba(255,255,255,0.95)", padding: "12px 16px",
              borderRadius: "8px", boxShadow: "0 2px 8px rgba(0,0,0,0.2)",
              fontSize: "12px", zIndex: 10,
            }}>
              <div style={{ fontWeight: "700", marginBottom: "8px", fontSize: "13px" }}>Rent Intensity</div>
              <div style={{ display: "flex", alignItems: "center", gap: "8px" }}>
                <div style={{
                  width: "150px", height: "14px", borderRadius: "3px",
                  background: "linear-gradient(to right, #00ff00, #ffff00, #ff8800, #ff0000)",
                }} />
              </div>
              <div style={{ display: "flex", justifyContent: "space-between", marginTop: "4px", color: "#666" }}>
                <span>Lower</span>
                <span>Higher</span>
              </div>
              {heatData?.total && (
                <div style={{ marginTop: "6px", color: "#999" }}>
                  {heatData.total.toLocaleString()} listings · last {heatData.months} months
                </div>
              )}
            </div>
          </div>
        )}
      </div>
    </div>
  );
}
